import { parsePersistedState, type PersistedState } from "./persist";

// 単一JSON文書の HTTP GET/PUT ミラー。サーバー側の契約は「GET = 最後に PUT された
// 本文 or 404 / PUT = 上書き」のみで、実装 (実機は Caddy + webdav) に依存しない。
// ライブラリ切り出し時はこのモジュールをそのまま持ち出し、URL を option 化する。
//
// InPrivate 等でブラウザ側ストレージが消えても、直近 PUT 済みの状態から継続する
// ための層。エンドポイントが無い環境 (pages.dev・開発時) は PUT の 404/405 を受けて
// 以降恒久無効化するので、既定で有効にしても安全。
// 注意: GET の 404 は「文書が未作成なだけ」(キオスク初回起動が該当) なので無効化しない。

const PULL_TIMEOUT_MS = 3_000;

export interface StateMirror {
  /** サーバー上の文書を取得。無い/失敗/形式不明は null (すべて正常系扱い) */
  pull(): Promise<PersistedState | null>;
  /** 文書を上書き送信 (fire-and-forget)。失敗は次回 push で自然リトライ */
  push(state: PersistedState): void;
}

export const createStateMirror = (url: string): StateMirror => {
  // PUT が 404/405 = 書き込みエンドポイントが無い環境。以降このセッションでは何もしない
  let disabled = false;

  const pull = async (): Promise<PersistedState | null> => {
    if (disabled || typeof fetch === "undefined") return null;
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), PULL_TIMEOUT_MS);
      const res = await fetch(url, { cache: "no-store", signal: ctrl.signal }).finally(() =>
        clearTimeout(timer),
      );
      if (!res.ok) return null;
      return parsePersistedState(await res.json());
    } catch {
      return null;
    }
  };

  const push = (state: PersistedState): void => {
    if (disabled || typeof fetch === "undefined") return;
    try {
      fetch(url, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(state),
      })
        .then((res) => {
          if (res.status === 404 || res.status === 405) disabled = true;
        })
        .catch(() => {
          // 一時的な失敗。次回 push で再試行
        });
    } catch {
      // noop
    }
  };

  return { pull, push };
};
